import { api, type ResetPasswordResponse } from '../lib/api-client';
import { fromApiResult, type DataResult } from '../lib/data-result';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  UserProfile,
} from '../types/api';

export type AuthRecoveryFlow = 'login' | 'register' | 'recovery' | 'reset-password';
export type AuthRecoveryOutcome = 'success' | 'error' | 'info';
export type AuthRecoveryTone = 'success' | 'error' | 'info';

export interface AuthRecoveryMessage {
  readonly tone: AuthRecoveryTone;
  readonly text: string;
  readonly actionHref?: string;
  readonly actionLabel?: string;
}

export interface AuthRecoveryMessageContext {
  readonly verification_email_sent?: boolean;
  readonly security_email_sent?: boolean;
}

export interface AuthFormValues {
  readonly isRegister: boolean;
  readonly username: string;
  readonly email?: string;
  readonly password: string;
}

export interface AuthFormState {
  readonly submitDisabled: boolean;
}

export interface PasswordResetRequestFeedback {
  readonly buttonHtml: string;
  readonly buttonDisabled: boolean;
}

export interface PasswordResetRequestFormValues {
  readonly email: string;
  readonly lastRequestedEmail?: string;
}

export interface PasswordResetRequestFormState {
  readonly submitDisabled: boolean;
}

export interface PasswordResetFormValues {
  readonly password: string;
  readonly confirm: string;
}

export interface PasswordResetFormState {
  readonly submitDisabled: boolean;
}

export type AuthSubmitSuccessFlow = 'login' | 'register';

function normalizedError(error?: string | null): string {
  return String(error || '').trim();
}

function includesAny(value: string, terms: string[]): boolean {
  const lower = value.toLowerCase();
  return terms.some(term => lower.includes(term));
}

export function normalizePasswordResetEmail(value: string): string {
  return value.trim().toLowerCase();
}

export function consumeOneTimeLoginNoticeUrl(href: string): string {
  const url = new URL(href);
  if (url.searchParams.get('reset') === 'success') {
    url.searchParams.delete('reset');
    url.searchParams.delete('security_email');
  }
  return url.toString();
}

export function buildAuthRecoveryMessage(
  flow: AuthRecoveryFlow,
  outcome: AuthRecoveryOutcome,
  error?: string | null,
  context?: AuthRecoveryMessageContext,
): AuthRecoveryMessage {
  if (outcome === 'success') {
    if (flow === 'register') {
      return {
        tone: 'success',
        text: context?.verification_email_sent
          ? 'Account created. Verification email sent; opening Settings so you can confirm it.'
          : 'Account created. Opening Settings so you can send a verification email.',
      };
    }
    if (flow === 'recovery') {
      return {
        tone: 'success',
        text: 'If that email is registered, reset instructions have been sent.',
      };
    }
    if (flow === 'reset-password') {
      const securityEmailText = context?.security_email_sent === true
        ? ' Security notice sent.'
        : context?.security_email_sent === false
          ? ' Security notice email was not sent.'
          : '';
      return {
        tone: 'success',
        text: `Password updated.${securityEmailText} You can sign in with the new password.`,
        actionHref: '/login',
        actionLabel: 'Sign in',
      };
    }
    return { tone: 'success', text: 'Welcome back. Redirecting now.' };
  }

  if (outcome === 'info') {
    return {
      tone: 'info',
      text: 'Sign in to continue where you left off.',
    };
  }

  const message = normalizedError(error);
  if (flow === 'login' && includesAny(message, ['invalid credentials'])) {
    return {
      tone: 'error',
      text: 'Could not sign in. Check your username or email and password, or reset your password.',
      actionHref: '/forgot-password',
      actionLabel: 'Reset password',
    };
  }
  if (flow === 'register' && includesAny(message, ['already exists', 'already in use'])) {
    return {
      tone: 'error',
      text: 'That username or email is already in use. Sign in or use a different email.',
      actionHref: '/login',
      actionLabel: 'Sign in',
    };
  }
  if (flow === 'login' && includesAny(message, ['account suspended'])) {
    return {
      tone: 'error',
      text: 'This SkillFab account is suspended. A maintainer must reactivate it before you can sign in; resetting your password will not restore access.',
    };
  }
  if (flow === 'reset-password' && includesAny(message, ['missing', 'invalid', 'expired', 'already been used', 'no longer valid'])) {
    return {
      tone: 'error',
      text: 'This reset link can no longer be used. Request a new password reset email.',
      actionHref: '/forgot-password',
      actionLabel: 'Request new link',
    };
  }
  if (includesAny(message, ['too many requests', 'rate limited'])) {
    return {
      tone: 'error',
      text: message || 'Too many requests. Please wait, then try again.',
    };
  }

  return {
    tone: 'error',
    text: message || (
      flow === 'register'
        ? 'Registration failed. Please check the fields and try again.'
        : flow === 'reset-password'
          ? 'Could not reset password. Please try again.'
          : flow === 'recovery'
            ? 'Could not request password reset. Please try again.'
            : 'Login failed. Please try again.'
    ),
  };
}

type AuthReturnTargetPath = '/login' | '/register' | '/forgot-password';

function safeSameSitePath(value?: string | null): string | null {
  const redirect = String(value || '').trim();
  if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
    return redirect;
  }
  return null;
}

export function buildPostRegisterRedirect(defaultRedirect: string, context: AuthRecoveryMessageContext): string {
  const redirect = safeSameSitePath(defaultRedirect);
  const params = new URLSearchParams({ focus: 'email-verification' });
  if (context.verification_email_sent === true) params.set('email_verification_sent', '1');
  if (redirect) params.set('next', redirect);
  return `/settings?${params.toString()}`;
}

export function getSafeAuthRedirect(search: string): string | null {
  return safeSameSitePath(new URLSearchParams(search).get('redirect'));
}

export function buildAuthReturnHref(targetPath: AuthReturnTargetPath, search: string): string {
  const redirect = getSafeAuthRedirect(search);
  if (redirect) return targetPath + '?redirect=' + encodeURIComponent(redirect);
  return targetPath;
}

export function buildAuthSwitchHref(targetPath: '/login' | '/register', search: string): string {
  return buildAuthReturnHref(targetPath, search);
}

export function buildAuthActionHref(actionHref: string, search: string): string {
  if (actionHref === '/login' || actionHref === '/register' || actionHref === '/forgot-password') {
    return buildAuthReturnHref(actionHref, search);
  }
  return actionHref;
}

export function buildPasswordResetSuccessLoginHref(search: string, result?: ResetPasswordResponse): string {
  const params = new URLSearchParams({ reset: 'success' });
  const redirect = getSafeAuthRedirect(search);
  if (redirect) params.set('redirect', redirect);
  if (result?.security_email_sent === true) params.set('security_email', 'sent');
  if (result?.security_email_sent === false) params.set('security_email', 'not_sent');
  return `/login?${params.toString()}`;
}

export function buildLoginNoticeFromSearch(search: string): AuthRecoveryMessage | null {
  const params = new URLSearchParams(search);
  if (params.get('reset') === 'success') {
    if (params.get('security_email') === 'sent') {
      return {
        tone: 'success',
        text: 'Password updated. Security notice sent. Sign in with your new password.',
      };
    }
    if (params.get('security_email') === 'not_sent') {
      return {
        tone: 'success',
        text: 'Password updated. Security notice email was not sent. Sign in with your new password.',
      };
    }
    return {
      tone: 'success',
      text: 'Password updated. Sign in with your new password.',
    };
  }
  if (params.has('redirect')) {
    return {
      tone: 'info',
      text: 'Sign in to continue where you left off.',
    };
  }
  return null;
}

export function buildPasswordResetRequestFeedback(): PasswordResetRequestFeedback {
  return {
    buttonHtml: '<i class="fas fa-check mr-2"></i>Reset email requested',
    buttonDisabled: true,
  };
}

export function buildPasswordResetRequestFormState(values: PasswordResetRequestFormValues): PasswordResetRequestFormState {
  const email = normalizePasswordResetEmail(values.email);
  const lastRequestedEmail = normalizePasswordResetEmail(values.lastRequestedEmail || '');
  return {
    submitDisabled: email.length === 0
      || !isBasicEmailReady(email)
      || (lastRequestedEmail.length > 0 && email === lastRequestedEmail),
  };
}

export function buildPasswordResetRequestHelpText(values: PasswordResetRequestFormValues): string {
  const email = normalizePasswordResetEmail(values.email);
  if (email.length > 0 && !isBasicEmailReady(email)) return 'Enter an email like you@example.com.';
  return '';
}

export function buildPasswordResetSubmitFeedback(): PasswordResetRequestFeedback {
  return {
    buttonHtml: '<i class="fas fa-check mr-2"></i>Password updated',
    buttonDisabled: true,
  };
}

export function buildPasswordResetFormState(values: PasswordResetFormValues): PasswordResetFormState {
  const password = values.password;
  const confirm = values.confirm;
  return {
    submitDisabled: password.length < 6 || confirm.length < 6 || password !== confirm,
  };
}

export function buildPasswordResetFormHelpText(values: PasswordResetFormValues): string {
  const password = values.password;
  const confirm = values.confirm;
  if (password.length > 0 && password.length < 6) return 'Password must be at least 6 characters.';
  if (confirm.length > 0 && confirm.length < 6) return 'Confirm password must be at least 6 characters.';
  if (password.length >= 6 && confirm.length >= 6 && password !== confirm) return 'Passwords do not match.';
  return '';
}

function isBasicEmailReady(email: string): boolean {
  const parts = email.trim().split('@');
  if (parts.length !== 2 || parts[0].length === 0 || parts[1].length === 0) return false;
  const domainParts = parts[1].split('.');
  return domainParts.length >= 2 && domainParts.every(part => part.length > 0);
}

export function buildAuthFormState(values: AuthFormValues): AuthFormState {
  const username = values.username.trim();
  const email = String(values.email || '').trim();
  return {
    submitDisabled: values.isRegister
      ? username.length < 3 || !isBasicEmailReady(email) || values.password.length < 6
      : username.length === 0 || values.password.length === 0,
  };
}

export function buildAuthFormHelpText(values: AuthFormValues): string {
  if (!values.isRegister) return '';
  const username = values.username.trim();
  const email = String(values.email || '').trim();
  if (username.length > 0 && username.length < 3) return 'Username must be at least 3 characters.';
  if (email.length > 0 && !isBasicEmailReady(email)) return 'Enter an email like you@example.com.';
  if (values.password.length > 0 && values.password.length < 6) return 'Password must be at least 6 characters.';
  return '';
}

export function buildAuthSubmitSuccessFeedback(flow: AuthSubmitSuccessFlow): PasswordResetRequestFeedback {
  if (flow === 'register') {
    return {
      buttonHtml: '<i class="fas fa-check mr-2"></i>Account created',
      buttonDisabled: true,
    };
  }
  return {
    buttonHtml: '<i class="fas fa-check mr-2"></i>Signed in',
    buttonDisabled: true,
  };
}

function persistBrowserAuthSession(result: { token: string; user: UserProfile }): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('skillfab_token');
  window.authToken = null;
  window.currentUser = result.user;
  window.updateNavAuth?.(true);
}

export async function login(request: LoginRequest): Promise<DataResult<LoginResponse>> {
  const result = await api.users.login(request);
  if (result.success) persistBrowserAuthSession(result.data);
  return fromApiResult(result, 'Login failed');
}

export async function register(request: RegisterRequest, search = ''): Promise<DataResult<RegisterResponse>> {
  const redirect = getSafeAuthRedirect(search);
  const result = await api.users.register(redirect ? { ...request, redirect } : request);
  if (result.success) persistBrowserAuthSession(result.data);
  return fromApiResult(result, 'Registration failed');
}

export async function requestPasswordReset(email: string, search = ''): Promise<DataResult<Record<string, unknown>>> {
  const redirect = getSafeAuthRedirect(search);
  const result = await api.users.requestPasswordReset(email, redirect ? { redirect } : undefined);
  return fromApiResult(result, 'Could not request password reset.');
}

export async function resetPassword(request: { token: string; password: string }): Promise<DataResult<ResetPasswordResponse>> {
  const result = await api.users.resetPassword(request);
  return fromApiResult(result, 'Could not reset password.');
}

if (typeof window !== 'undefined') {
  window.LoginPageData = {
    buildAuthRecoveryMessage,
    buildLoginNoticeFromSearch,
    buildPostRegisterRedirect,
    buildAuthReturnHref,
    buildAuthSwitchHref,
    buildAuthActionHref,
    buildPasswordResetSuccessLoginHref,
    buildAuthSubmitSuccessFeedback,
    getSafeAuthRedirect,
    normalizePasswordResetEmail,
    consumeOneTimeLoginNoticeUrl,
    buildPasswordResetRequestFeedback,
    buildPasswordResetRequestFormState,
    buildPasswordResetRequestHelpText,
    buildPasswordResetSubmitFeedback,
    buildPasswordResetFormState,
    buildPasswordResetFormHelpText,
    buildAuthFormState,
    buildAuthFormHelpText,
    login,
    register,
    requestPasswordReset,
    resetPassword,
  };
}
